<template>
  <div>
    <div class="content-header">
      <div class="container-fluid">
        <div class="row mb-2">
          <div class="col-md-6 col-sm-12">
            <h4 class="font-weight-light">
              <span class="font-weight-bold">TikTok Seller Own Fleet</span>
            </h4>
          </div>
        </div>
      </div>
    </div>

    <section class="content">
      <div class="container-fluid">
        <StepTracker :steps="steps"
                     :currentScreen="currentScreen"
                     @step-change="handleStepChange" />

        <!-- Screen 1: File Upload -->
        <div v-if="currentScreen === 1">
          <UploadScreen :shops="shops"
                        :shippingMethods="shippingMethods"
                        @file-uploaded="handleFileUploaded" />
        </div>

        <!-- Screen 2: File Uploaded Successfully -->
        <div v-if="currentScreen === 2">
          <FileUploadedScreen :rowCount="uploadedRowCount"
                              :fileName="uploadedFileName"
                              @validate-addresses="handleValidateAddresses" />
        </div>

        <!-- Screen 3: Address Validation -->
        <div v-if="currentScreen === 3">
          <AddressValidationScreen :processedAddresses="processedAddresses"
                                   @proceed-to-create="handleProceedToCreate"
                                   @address-corrected="handleAddressCorrected" />
        </div>

        <!-- Screen 4: Creating Orders -->
        <div v-if="currentScreen === 4">
          <CreatingOrdersScreen :progress="progress"
                                :progressText="progressText" />
        </div>

        <!-- Screen 5: Results -->
        <div v-if="currentScreen === 5">
          <ResultsScreen :successResults="successResults"
                         :errorResults="errorResults" />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import StepTracker from "./StepTracker.vue";
import UploadScreen from "./UploadScreen.vue";
import FileUploadedScreen from "./FileUploadedScreen.vue";
import AddressValidationScreen from "./AddressValidationScreen.vue";
import CreatingOrdersScreen from "./CreatingOrdersScreen.vue";
import ResultsScreen from "./ResultsScreen.vue";
import { isValidTrackingNo } from "./helpers";

export default {
  name: "TikTokSof",
  components: {
    StepTracker,
    UploadScreen,
    FileUploadedScreen,
    AddressValidationScreen,
    CreatingOrdersScreen,
    ResultsScreen
  },
  data() {
    return {
      currentScreen: 1,
      uploadedRowCount: 0,
      uploadedFileName: "",
      csvData: [],
      groupedOrders: [],
      processedAddresses: [],
      progress: 0,
      progressText: "",
      successResults: [],
      errorResults: [],
      selectedShopId: null,
      selectedShippingMethod: null,
      defaultWeight: 500,
      rawDatabase: null,
      steps: [
        { id: 1, title: "อัพโหลดไฟล์", desc: "เลือกไฟล์ CSV" },
        { id: 2, title: "ตรวจสอบไฟล์", desc: "ยืนยันข้อมูล" },
        { id: 3, title: "ตรวจสอบที่อยู่", desc: "แก้ไขถ้าจำเป็น" },
        { id: 4, title: "สร้างออเดอร์", desc: "กำลังประมวลผล" },
        { id: 5, title: "ผลลัพธ์", desc: "สรุปสำเร็จ/ผิดพลาด" }
      ]
    };
  },
  computed: {
    ...mapGetters({
      shops: "shops/shops",
      generalSettings: "settings/generalSettings",
      shippingMethods: "settings/shippingMethods"
    })
  },
  async created() {
    // Load shops and settings
    this.$store.dispatch("shops/fetchShops", { query: "responseSize=all" });
    this.$store.dispatch("settings/fetchGeneralSettings");
    this.$store.dispatch("settings/fetchShippingMethods");

    // Load raw_database.json
    await this.loadRawDatabase();
  },


  methods: {
    async loadRawDatabase() {
      try {
        const response = await fetch('https://proship-dev.s3.ap-southeast-1.amazonaws.com/raw_database.json');
        const rawData = await response.json();

        // rawData is already an array, just store it
        this.rawDatabase = Array.isArray(rawData) ? rawData : Object.entries(rawData).map(([zipcode, entry]) => ({
          zipcode: parseInt(zipcode),
          province: entry.province_th || entry.province,
          amphoe: entry.amphoe_th || entry.amphoe,
          district: entry.district_th || entry.district
        }));

        console.log('Raw database loaded:', this.rawDatabase.length, 'entries');
        console.log('Sample entry:', this.rawDatabase[0]);
      } catch (error) {
        console.error('Failed to load raw_database.json:', error);
        this.$swal({
          icon: 'error',
          title: 'ไม่สามารถโหลดฐานข้อมูลที่อยู่',
          text: 'ไม่สามารถเชื่อมต่อกับฐานข้อมูลที่อยู่ กรุณาลองใหม่อีกครั้ง'
        });
      }
    },

    handleFileUploaded(data) {
      this.uploadedRowCount = data.rowCount;
      this.uploadedFileName = data.fileName;
      this.csvData = data.csvData;
      this.groupedOrders = data.groupedOrders;
      this.selectedShopId = data.shopId;
      this.selectedShippingMethod = data.shippingMethod;
      this.defaultWeight = data.defaultWeight || 500;
      this.currentScreen = 2;
    },
    handleStepChange(target) {
      this.currentScreen = target;
    },
    handleValidateAddresses() {
      // Process addresses validation
      this.processAddresses();
      this.currentScreen = 3;
    },
    handleAddressCorrected(data) {
      // Update the processed address
      this.processedAddresses[data.index] = data.address;
    },
    handleProceedToCreate() {
      this.currentScreen = 4;
      this.createOrders();
    },
    processAddresses() {
      if (!this.rawDatabase) {
        this.$swal({
          icon: 'error',
          title: 'ฐานข้อมูลที่อยู่ยังไม่พร้อม',
          text: 'กรุณารอสักครู่แล้วลองใหม่อีกครั้ง'
        });
        return;
      }

      // Initialize with loading state
      this.processedAddresses = this.groupedOrders.map(order => {
        const row = order.baseRow;
        return {
          orderId: order.orderId,
          recipient: row['Recipient'] || '',
          originalAddress: row['Detail Address'] || '',
          providedData: {
            zipcode: row['Zipcode'] || '',
            province: row['Province'] || '',
            district: row['District'] || '',
            additional: row['Additional address information'] || ''
          },
          processed: {
            final: {
              zipcode: '',
              province: '',
              district: '',
              subdistrict: ''
            },
            status: 'loading' // Show loading state
          },
          items: order.items,
          totalWeightGrams: order.items.reduce((sum, item) => {
            return sum + (item.weightKg * 1000 * item.qty);
          }, 0) || this.defaultWeight,
          originalRow: row
        };
      });

      // Parse each address asynchronously
      this.processedAddresses.forEach((address, index) => {
        this.parseAddressAsync(address, index);
      });
    },

    async parseAddressAsync(address, index) {
      try {
        const row = address.originalRow;
        const addressText = row['Detail Address'] || '';
        const providedData = {
          zipcode: row['Zipcode'] || '',
          province: row['Province'] || '',
          district: row['District'] || '',
          additional: row['Additional address information'] || ''
        };

        const processed = this.processThaiAddress(addressText, providedData);

        this.updateAddressStatus(index, processed.final, processed.status);
      } catch (error) {
        console.error('Error parsing address:', error);
        const row = address.originalRow;
        this.updateAddressStatus(index, {
          zipcode: row['Zipcode'] || '',
          province: row['Province'] || '',
          district: row['District'] || '',
          subdistrict: row['Additional address information'] || ''
        }, 'invalid');
      }
    },

    processThaiAddress(addressText, providedData) {
      // Combine address text with additional info for processing
      const fullText = `${addressText} ${providedData.additional}`.trim();

      // Extract zipcode using multiple regex patterns
      const zipcodePatterns = [
        /\b(\d{5})\b/g,           // word boundaries
        /\s(\d{5})\s/g,           // space boundaries
        /\s(\d{5})$/g,            // space + end of string
        /^(\d{5})\s/g,            // start + space
        /[^\d](\d{5})[^\d]/g,     // non-digit boundaries
        /(\d{5})/g                // any 5 consecutive digits
      ];

      let extractedZipcode = null;
      for (const pattern of zipcodePatterns) {
        const match = fullText.match(pattern);
        if (match) {
          extractedZipcode = match[0].replace(/\D/g, ''); // Remove non-digits
          break;
        }
      }

      // Use provided zipcode if no extraction successful
      if (!extractedZipcode && providedData.zipcode) {
        extractedZipcode = providedData.zipcode.toString().replace(/\D/g, '');
      }

      // Extract subdistrict using patterns
      const subdistrictPatterns = [
        /ตำบล([^\s,\n]+)/g,       // ตำบล prefix
        /ต\.([^\s,\n]+)/g,        // ต. abbreviation
        /แขวง([^\s,\n]+)/g        // แขวง for Bangkok
      ];

      let extractedSubdistrict = null;
      for (const pattern of subdistrictPatterns) {
        const match = fullText.match(pattern);
        if (match) {
          extractedSubdistrict = match[0].replace(/(ตำบล|ต\.|แขวง)/, '').trim();
          break;
        }
      }

      // Extract district patterns
      const districtPatterns = [
        /อำเภอ([^\s,\n]+)/g,      // อำเภอ prefix
        /อ\.([^\s,\n]+)/g,        // อ. abbreviation
        /เขต([^\s,\n]+)/g         // เขต for Bangkok
      ];

      let extractedDistrict = null;
      for (const pattern of districtPatterns) {
        const match = fullText.match(pattern);
        if (match) {
          extractedDistrict = match[0].replace(/(อำเภอ|อ\.|เขต)/, '').trim();
          break;
        }
      }

      // Validate against database
      const validation = this.validateAgainstDatabase(
        extractedZipcode || providedData.zipcode,
        providedData.province,
        extractedDistrict || providedData.district,
        extractedSubdistrict
      );

      return {
        extracted: {
          zipcode: extractedZipcode,
          subdistrict: extractedSubdistrict,
          district: extractedDistrict
        },
        final: {
          zipcode: validation.zipcode || providedData.zipcode,
          province: validation.province || providedData.province,
          district: validation.district || providedData.district,
          subdistrict: validation.subdistrict || extractedSubdistrict || providedData.additional
        },
        validation: validation,
        status: validation.isValid ? 'valid' : (validation.isPartial ? 'partial' : 'invalid')
      };
    },

    validateAgainstDatabase(zipcode, province, district, subdistrict) {
      if (!zipcode || !this.rawDatabase) {
        console.log('Validation failed: no zipcode or database', { zipcode, hasDB: !!this.rawDatabase });
        return { isValid: false, isPartial: false };
      }

      const zipcodeNum = parseInt(zipcode.toString().replace(/\D/g, ''));
      const matches = this.rawDatabase.filter(item => item.zipcode === zipcodeNum);

      console.log('Validation:', {
        zipcode: zipcodeNum,
        provided: { province, district, subdistrict },
        matchCount: matches.length,
        firstMatch: matches[0]
      });

      if (matches.length === 0) {
        console.log('No matches found for zipcode:', zipcodeNum);
        return { isValid: false, isPartial: false };
      }

      // Try to find exact match
      let bestMatch = matches[0];
      let score = 0;

      matches.forEach(match => {
        let currentScore = 1; // Base score for zipcode match

        if (province && match.province.includes(province)) {
          currentScore += 2;
        }
        if (district && match.amphoe.includes(district)) {
          currentScore += 2;
        }
        if (subdistrict && match.district.includes(subdistrict)) {
          currentScore += 3;
        }

        if (currentScore > score) {
          score = currentScore;
          bestMatch = match;
        }
      });

      console.log('Best match:', { score, match: bestMatch });

      return {
        isValid: score >= 3,
        isPartial: score >= 1 && score < 3,
        zipcode: bestMatch.zipcode.toString(),
        province: bestMatch.province,
        district: bestMatch.amphoe,
        subdistrict: bestMatch.district,
        matchScore: score,
        allMatches: matches.length
      };
    },

    updateAddressStatus(index, finalData, status) {
      if (this.processedAddresses[index]) {
        this.processedAddresses[index].processed.final = finalData;
        this.processedAddresses[index].processed.status = status;
        // Trigger reactivity
        this.$set(this.processedAddresses, index, this.processedAddresses[index]);
      }
    },

    extractTrackingNo(orderResponse) {
      if (!orderResponse) return null;
      const details = orderResponse.details || {};
      const candidate =
        orderResponse.trackingNo ||
        details.trackingNo ||
        orderResponse.manifestNo ||
        details.manifestNo ||
        orderResponse.tracking_number ||
        details.tracking_number ||
        null;
      return isValidTrackingNo(candidate) ? String(candidate).trim() : null;
    },

    async createOrders() {
      this.successResults = [];
      this.errorResults = [];

      for (let i = 0; i < this.processedAddresses.length; i++) {
        const addressData = this.processedAddresses[i];
        const progress = Math.round(((i + 1) / this.processedAddresses.length) * 100);

        this.progress = progress;
        this.progressText = `กำลังสร้างออเดอร์ ${i + 1}/${this.processedAddresses.length}`;

        // Add 800ms delay per order
        await new Promise(resolve => setTimeout(resolve, 800));

        try {
          const orderData = this.createOrderData(addressData);
          const result = await this.callProshipAPI(orderData);

          let trackingNumber = this.extractTrackingNo(result);
          const cleanProshipId = result.id && result.id.includes('|') ? result.id.split('|')[0] : result.id;

          // If not in creation response, fetch order details with a short retry
          if (!trackingNumber && cleanProshipId) {
            const retryDelays = [200, 500];
            for (const delay of retryDelays) {
              try {
                await new Promise(resolve => setTimeout(resolve, delay));
                const orderDetails = await this.fetchOrderDetails(cleanProshipId);
                trackingNumber = this.extractTrackingNo(orderDetails);
                if (trackingNumber) break;
              } catch (detailError) {
                console.log('Could not fetch order details:', detailError);
              }
            }
          }

          this.successResults.push({
            orderId: addressData.orderId,
            proshipId: cleanProshipId,
            fullProshipId: result.id,
            trackingNo: trackingNumber,
            recipient: addressData.recipient
          });
        } catch (error) {
          this.errorResults.push({
            orderId: addressData.orderId,
            recipient: addressData.recipient,
            error: error.message || 'Unknown error'
          });
        }
      }

      this.currentScreen = 5;
    },

    createOrderData(addressData) {
      const row = addressData.originalRow;
      const processedAddr = addressData.processed.final;
      const items = addressData.items || [];

      let phoneNo = row['Phone #'] || '';
      phoneNo = phoneNo.replace(/[^\d]/g, '');
      if (phoneNo.startsWith('66')) {
        const remainingDigits = phoneNo.substring(2);
        phoneNo = remainingDigits.startsWith('0') ? remainingDigits : '0' + remainingDigits;
      }

      // Group items by SKU and combine quantities
      const skuMap = new Map();
      items.forEach((item) => {
        const sku = item.sku || item.name;
        if (skuMap.has(sku)) {
          // Add to existing SKU quantity
          skuMap.get(sku).qty += item.qty || 1;
        } else {
          // Create new SKU entry
          skuMap.set(sku, {
            name: item.name,
            sku: sku,
            qty: item.qty || 1,
            variation: item.variation,
          });
        }
      });

      // Convert map to array with sortIndex
      const productItems = Array.from(skuMap.values()).map((item, index) => ({
        sku: item.sku,
        name: item.name,
        qty: item.qty,
        variation: item.variation,
        sortIndex: index + 1
      }));

      const allProductNames = items.map(item => item.name).join(', ');
      const weight = addressData.totalWeightGrams || this.defaultWeight;

      return {
        shippingMethod: this.selectedShippingMethod,
        weight: Math.round(weight),
        shopId: this.selectedShopId,
        orderCode: addressData.orderId,
        custRef: addressData.orderId,
        remarks: allProductNames || '',
        isTiktokSof: true,
        customer: {
          name: row['Recipient'] || 'ไม่ระบุชื่อ',
          phoneNo: phoneNo,
          salesChannel: 'csv-upload',
          address: {
            address: ((row['Additional address information'] || '') + ' ' + (row['Detail Address'] || '')).trim(),
            province: processedAddr.province || '',
            district: processedAddr.district || '',
            subDistrict: processedAddr.subdistrict || '',
            zipcode: parseInt(processedAddr.zipcode) || 10000
          }
        },
        productItems: productItems,
        products: {}
      };
    },
    async callProshipAPI(orderData) {
      console.log('Creating order with data:', orderData);
      return new Promise((resolve, reject) => {
        this.$store.dispatch("orders/createOrder", {
          order: orderData,
          callback: (status, data) => {
            if (status && data) {
              resolve(data);
            } else {
              reject(new Error(data?.message || 'Failed to create order'));
            }
          }
        });
      });
    },
    async fetchOrderDetails(orderId) {
      return new Promise((resolve, reject) => {
        this.$store.dispatch("orders/fetchOrderDetail", {
          id: orderId,
          callback: (status, data) => {
            if (status && data) {
              resolve(data);
            } else {
              reject(new Error('Failed to fetch order details'));
            }
          }
        });
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.step-tracker {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.step-item {
  cursor: pointer;
  padding: 8px 10px;
  border-radius: 8px;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.step-item.is-disabled {
  opacity: 0.55;
  cursor: default;
}

.step-item.is-active {
  background: #e3f2fd;
  border-color: #90caf9;
}

.step-item.is-done {
  background: #e8f5e9;
  border-color: #a5d6a7;
}
</style>
